/**
 * 노드/엣지 색상 팔레트.
 *
 * 원본: `src/dashboard/src/components/graph/graph-colors.ts` 에서 shared 로 이전.
 * pion-knowledge 온톨로지 (src/ontology/schema.ts) 의 kind/domain/relation 에 맞춰
 * 보강 가능 — 미등록 값은 `default` 로 폴백한다.
 */

/** 도메인별 노드 색상 */
export const DOMAIN_COLORS: Record<string, string> = {
	aws: "#ff9900",
	slack: "#4a154b",
	github: "#238636",
	jira: "#0052cc",
	confluence: "#172b4d",
	notion: "#000000",
	linear: "#5e6ad2",
	figma: "#f24e1e",
	infra: "#3a86ff",
	docs: "#06b6d4",
	default: "#8b949e"
}

/** 카테고리별 노드 색상 */
export const CATEGORY_COLORS: Record<string, string> = {
	SOFTWARE: "#238636",
	INFRASTRUCTURE: "#ff9900",
	EVIDENCE: "#d2a8ff",
	EXTERNAL: "#bc8cff",
	ORGANIZATION: "#f59e0b",
	COMMUNICATION: "#ec4899",
	KNOWHOW: "#7ee787",
	APPLICATION: "#238636",
	DATA: "#3b82f6",
	SECURITY: "#ef4444",
	NETWORK: "#8b5cf6",
	PERSON: "#ec4899",
	DOCUMENT: "#06b6d4",
	CONCEPT: "#a78bfa",
	EVENT: "#14b8a6"
}

/** 관계별 엣지 색상 */
export const RELATION_COLORS: Record<string, string> = {
	// 레거시 lowercase
	depends_on: "#f97316",
	uses: "#3b82f6",
	connects_to: "#22c55e",
	belongs_to: "#8b5cf6",
	manages: "#ef4444",
	monitors: "#eab308",
	// 구조적
	BELONGS_TO: "#6e40c9",
	CONTAINS: "#ff9f1c",
	DEFINED_IN: "#8b949e",
	PART_OF: "#a78bfa",
	HAS_FEATURE: "#8b5cf6",
	// 코드/의존
	DEPENDS_ON: "#3a86ff",
	IMPORTS: "#58a6ff",
	// 데이터 접근
	READS_FROM: "#06ffa5",
	WRITES_TO: "#f7b32b",
	CACHES_WITH: "#14b8a6",
	// API/네트워크
	CALLS_API: "#f94144",
	ROUTES_TO: "#fb5607",
	EXPOSES: "#60a5fa",
	// 메시징
	PUBLISHES_TO: "#ff006e",
	CONSUMES_FROM: "#db2777",
	// 사람
	MEMBER_OF: "#a78bfa",
	REPORTS_TO: "#c084fc",
	OWNS: "#f59e0b",
	// 증거/참조
	RELATES_TO: "#6366f1",
	REFERENCES: "#79c0ff",
	MENTIONED_IN: "#c678dd",
	MENTIONS: "#79c0ff",
	// 제품 관리
	MEASURES: "#34d399",
	TARGETS: "#c026d3",
	SPECIFIES: "#fbbf24",
	SCHEDULED_IN: "#1e40af",
	MILESTONE_FOR: "#f59e0b",
	JOURNEY_INCLUDES: "#22d3ee",
	FEATURE_SURFACED_ON: "#10b981",
	VIEW_USES: "#14b8a6",
	VIEW_BELONGS_TO: "#10b981",
	PERSONA_USES: "#ec4899",
	TRACKS: "#0891b2",
	// sales 팩
	WON_BY: "#f59e0b",
	CONVERTED_TO: "#fbbf24",
	QUALIFIED_BY: "#eab308",
	SPONSORED_BY: "#d97706",
	// marketing 팩
	CAMPAIGN_TARGETS: "#db2777",
	NURTURES: "#e11d48",
	AUDIENCE_OF: "#ec4899",
	SURFACED_IN: "#f472b6",
	// design 팩
	IMPLEMENTS_PATTERN: "#14b8a6",
	TOKENIZED_BY: "#2dd4bf",
	MOCKED_AS: "#67e8f9",
	// decisions 팩 (cross-cutting)
	DECIDED_BY: "#fbbf24",
	APPROVED_BY: "#fde047",
	AFFECTS: "#f97316",
	// 외부
	DEPENDS_ON_EXTERNAL: "#ef4444",
	AUTHENTICATES_VIA: "#a855f7",
	WEBHOOKS_TO: "#fb7185",
	// literature 팩 (해리포터 데모용)
	FRIEND_OF: "#22d3ee",
	ENEMY_OF: "#ef4444",
	MEMBER_OF_HOUSE: "#8b5cf6",
	STUDIES_AT: "#3b82f6",
	OWNED_BY: "#f59e0b",
	BONDED_TO: "#14b8a6",
	WIELDS: "#f97316",
	GUARDS: "#238636",
	default: "#4b5563"
}

/** Kind 별 상세 색상 매핑 */
export const KIND_COLORS: Record<string, string> = {
	// core-org
	org: "#6e40c9",
	team: "#8b5cf6",
	person: "#a78bfa",
	persona: "#ec4899",
	role: "#c084fc",
	// core-comms
	channel: "#ec4899",
	page: "#0ea5e9",
	document: "#06b6d4",
	thread: "#22d3ee",
	// core-external
	external_api: "#ef4444",
	saas_service: "#f97316",
	vendor: "#fb923c",
	// engineering
	repository: "#238636",
	service: "#2ea043",
	module: "#3fb950",
	library: "#3fb950",
	package: "#56d364",
	workflow: "#1f6feb",
	api_endpoint: "#58a6ff",
	release: "#0366d6",
	deployment: "#1f6feb",
	source_file: "#34d058",
	code_function: "#79c0ff",
	code_class: "#28a745",
	code_type: "#58a6ff",
	test_suite: "#a3e635",
	dataset: "#79c0ff",
	data_pipeline: "#39d353",
	// infrastructure
	compute_cluster: "#ff9f1c",
	compute_service: "#ff6b35",
	serverless_function: "#f7b32b",
	relational_db: "#3a86ff",
	document_db: "#60a5fa",
	db_schema: "#93c5fd",
	cache: "#06ffa5",
	object_store: "#00b4d8",
	container_registry: "#0891b2",
	load_balancer: "#fb5607",
	cdn: "#8338ec",
	api_gateway: "#f94144",
	message_queue: "#ff006e",
	secret_store: "#a855f7",
	identity_provider: "#ec4899",
	target_group: "#f472b6",
	vpc: "#6366f1",
	security_group: "#8b5cf6",
	route53_zone: "#a78bfa",
	sns_topic: "#fb7185",
	parameter_store: "#a855f7",
	environment: "#22d3ee",
	// product
	product: "#6f42c1",
	feature: "#8b5cf6",
	view: "#10b981",
	user_journey: "#22d3ee",
	product_goal: "#c026d3",
	product_metric: "#34d399",
	specification: "#fbbf24",
	milestone: "#f59e0b",
	jira_issue: "#0052cc",
	jira_epic: "#172b4d",
	jira_sprint: "#1e40af",
	roadmap: "#a855f7",
	// sales (GTM) — 노란·주황 계열로 조직 외부 고객 맥락 분리
	account: "#eab308",
	lead: "#fde047",
	opportunity: "#facc15",
	deal: "#f59e0b",
	contract: "#d97706",
	pipeline_stage: "#fbbf24",
	sales_meeting: "#fcd34d",
	customer_segment: "#fb923c",
	quota: "#f97316",
	sales_metric: "#eab308",
	// marketing (GTM) — 자홍·핑크 계열로 브랜드 맥락
	campaign: "#db2777",
	marketing_asset: "#f472b6",
	audience: "#ec4899",
	brand: "#be185d",
	funnel: "#e11d48",
	experiment: "#f43f5e",
	marketing_channel: "#f9a8d4",
	content_piece: "#fb7185",
	landing_page: "#fda4af",
	marketing_metric: "#db2777",
	// design (craft) — 청록·민트 계열로 UX 레이어 분리
	design_system: "#14b8a6",
	design_token: "#2dd4bf",
	design_component: "#5eead4",
	design_file: "#67e8f9",
	wireframe: "#99f6e4",
	mockup: "#a7f3d0",
	ux_pattern: "#6ee7b7",
	brand_asset: "#f472b6",
	design_review: "#0d9488",
	// decisions (cross-cutting)
	decision: "#fbbf24",
	// evidence/knowhow (copilot 전용이지만 pion-knowledge 에서도 색은 유지)
	incident: "#f85149",
	runbook: "#6f42c1",
	evidence: "#d2a8ff",
	knowhow: "#7ee787",
	// literature 팩 (해리포터 데모용)
	character: "#a78bfa",
	creature: "#ef4444",
	magical_object: "#f97316",
	location: "#238636",
	school: "#0052cc",
	house: "#8b5cf6",
	// place 계열 — 장소/행정구역 (녹색 계열, environment 와 동일 레이어 그룹)
	place: "#16a34a",
	administrative_area: "#15803d",
	// actor — AI Agent (보라 계열, 자동화 행위자)
	ai_agent: "#7c3aed",
	// product — 산출물(유물/아티팩트)
	artifact: "#a78bfa",
	// event 계열 — 변경/이벤트/이상/보안 (incident·release 와 같은 컨텍스트 톤)
	event: "#14b8a6",
	change_record: "#0d9488",
	performance_anomaly: "#f59e0b",
	security_finding: "#dc2626",
	// content — 컨텍스트 노트
	context_note: "#22d3ee",
	// structure(data) — 데이터 카탈로그/피드/소스 모듈
	data_catalog: "#60a5fa",
	data_feed: "#3b82f6",
	source_module: "#2ea043",
	// structure — pull request (GitHub 보라)
	pull_request: "#8957e5",
	// structure(metric) — 수치값/평점/스코어카드
	quantitative_value: "#34d399",
	rating: "#fbbf24",
	scorecard: "#10b981",
	default: "#6e7681"
}
